import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCentralStore } from "../../store/Central";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { useConnectStore } from "../../store/Connect";
import { useJobStore } from "../../store/Jobs";
import { InboxWidget } from "@/components/src/inbox/inbox-widget.component";
import {
	DeliveriesInboxWidget,
	ActiveDelivery,
} from "@/components/src/deliveries-inbox/deliveries-inbox-widget.component";
import { RecentFilesWidget } from "@/components/src/recent-files/recent-files-widget.component";
import { FileViewerModal } from "@/components/src/file-viewer/file-viewer-modal/file-viewer-modal.component";
import { supabase } from "@/lib/supabase";
import { DeliveryStatusEnum } from "@/lib/types/delivery.types";
import { getRecentFiles } from "@/lib/utils/recents";

export const ClientDashboardPage: React.FC = () => {
	const { clientId } = useParams<{ clientId: string }>();
	const navigate = useNavigate();

	const [activeDeliveries, setActiveDeliveries] = useState<ActiveDelivery[]>(
		[]
	);
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

	const client = useCentralStore((s) => s.client);
	const organization = useCentralStore((s) => s.organization);
	const profile = useCentralStore((s) => s.profile);
	const clientsLookup = useCentralStore((s) => s.clientsLookup);

	const latestChannels = useRealtimeStore((s) => s.latestChannels);
	const getNotificationNavigationPath = useRealtimeStore(
		(s) => s.getNotificationNavigationPath
	);
	const updateChannelReadState = useRealtimeStore(
		(s) => s.updateChannelReadState
	);
	const profilesForChat = useRealtimeStore((s) => s.profilesForChat);

	const brandsByOrgId = useConnectStore((s) => s.brandsByOrgId);

	const jobRequestChannels = useJobStore((s) => s.jobRequestChannels);
	const fetchJobRequestChannels = useJobStore(
		(s) => s.fetchJobRequestChannels
	);

	const connectedOrgId = client?.connected_organization_id ?? null;

	useEffect(() => {
		if (!clientId || !organization?.id) return;
		fetchActiveDeliveries();
		if (connectedOrgId) {
			fetchJobRequestChannels(connectedOrgId);
		}
	}, [clientId, organization?.id, connectedOrgId]);

	const fetchActiveDeliveries = async () => {
		if (!organization?.id || !clientId) return;
		const { data } = await supabase
			.from("deliveries")
			.select("*, jobs!inner(code, title, client_id)")
			.eq("organization_id", organization.id)
			.eq("jobs.client_id", clientId)
			.in("status", [
				DeliveryStatusEnum.PENDING,
				DeliveryStatusEnum.IN_TRANSIT,
			])
			.order("scheduled_departure_at", {
				ascending: true,
				nullsFirst: false,
			});

		if (data) {
			setActiveDeliveries(
				data.map((d: any) => ({
					delivery_id: d.id,
					job_id: d.job_id,
					job_code: d.jobs?.code ?? null,
					job_title: d.jobs?.title ?? null,
					status: d.status,
					courier_type: d.courier_type,
					courier_company_name: d.courier_company_name,
					description: d.description,
					scheduled_departure_at: d.scheduled_departure_at,
					created_at: d.created_at,
					client_id: d.jobs?.client_id ?? null,
				}))
			);
		}
	};

	const clientChannels = useMemo(() => {
		if (!clientId) return [];
		return latestChannels.filter(
			(ch) =>
				ch.job_client_id === clientId ||
				(connectedOrgId && ch.customer_org_id === connectedOrgId)
		);
	}, [latestChannels, clientId, connectedOrgId]);

	const clientJobRequestChannels = useMemo(() => {
		if (!connectedOrgId) return [];
		return jobRequestChannels.filter(
			(ch) => ch.customerOrgId === connectedOrgId
		);
	}, [jobRequestChannels, connectedOrgId]);

	const recentFiles = useMemo(
		() => getRecentFiles().filter((f) => f.clientId === clientId),
		[clientId]
	);

	const getOrgName = (orgId: string): string | undefined => {
		const brand = brandsByOrgId[orgId];
		if (brand?.name) return brand.name;
		const c = Object.values(clientsLookup).find(
			(cl) => cl.connected_organization_id === orgId
		);
		return c?.company_name || undefined;
	};

	const handleDeliveryClick = (delivery: ActiveDelivery) => {
		if (delivery.client_id && delivery.job_id) {
			navigate(`/client/${delivery.client_id}/job/${delivery.job_id}`);
		}
	};

	return (
		<div
			className="h-full p-3 grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-1 gap-3"
			data-testid="client-dashboard"
			aria-label="Kunden-Dashboard"
		>
			<InboxWidget
				channels={clientChannels}
				getNavigationPath={
					getNotificationNavigationPath ?? ((ch) => `/chats/${ch.id}`)
				}
				onMarkAsRead={updateChannelReadState}
				organizationId={organization?.id ?? ""}
				getOrgName={getOrgName}
				userId={profile?.id ?? ""}
				profilesForChat={profilesForChat}
				className="min-h-0"
				hideSubtitle
				showJobRequestsFilter={true}
				jobRequestChannels={clientJobRequestChannels}
			/>

			<div className="min-h-0 flex flex-col gap-3">
				<DeliveriesInboxWidget
					deliveries={activeDeliveries}
					onDeliveryClick={handleDeliveryClick}
					className="flex-1 min-h-0"
				/>
				<RecentFilesWidget
					files={recentFiles}
					onOpenFile={(fileId) => setSelectedFileId(fileId)}
					onNavigateToJob={(cId, jobId) =>
						navigate(`/client/${cId}/job/${jobId}`)
					}
					onOpenArchive={
						clientId
							? () => navigate(`/client/${clientId}/dateiarchiv`)
							: undefined
					}
					className="flex-1 min-h-0"
				/>
			</div>

			{selectedFileId && (
				<FileViewerModal
					fileIds={[selectedFileId]}
					isOpen={!!selectedFileId}
					onClose={() => setSelectedFileId(null)}
					metadata={recentFiles.find(
						(f) => f.fileId === selectedFileId
					)}
				/>
			)}
		</div>
	);
};
